import { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  TextField,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import ReplyIcon from "@mui/icons-material/Reply";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SendIcon from "@mui/icons-material/Send";
// import AdminLayout from "../layouts/AdminLayout";
import formatDate from "../components/FormatDate";
import AddModal from "../components/AddModal";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import { API_BASE_URL } from "../config";

import { CommentOutlinedIcon } from "../access/icons/Icons";

function Comments(props) {
  const { refreshNotifications } = props;

  const [comments, setComments] = useState([]);
  const [expandedIds, setExpandedIds] = useState([]);
  const [replyOpenId, setReplyOpenId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [showSidebar, setShowSidebar] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCommentId, setSelectedCommentId] = useState(null);

  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role");

  const fetchComments = () => {
    fetch(`${API_BASE_URL}/api/${role}/comments`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setComments(data);
          data.forEach((comment) => {
            fetchReplies(comment.id);
          });
        } else {
          setComments([]);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch comments:", err);
        setComments([]);
      });
  };

  const fetchReplies = async (commentId) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/comments/replies/${commentId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
        }
      );
      if (res.ok) {
        const replies = await res.json();
        setComments((prev) =>
          prev.map((c) => (c.id === commentId ? { ...c, replies } : c))
        );
      } else {
        console.error("Failed to fetch replies");
      }
    } catch (err) {
      console.error("Error fetching replies:", err);
    }
  };

  useEffect(() => {
    fetchComments();
  }, []);

  const handleDelete = async () => {
    try {
      const role = localStorage.getItem("role");

      const res = await fetch(
        `${API_BASE_URL}/api/${role}/comments/${selectedCommentId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
        }
      );

      if (res.ok) {
        setComments((prev) => prev.filter((c) => c.id !== selectedCommentId));
      } else {
        const err = await res.json();
        alert(err.error || "Failed to delete comment");
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
      alert("An unexpected error occurred.");
    } finally {
      setModalOpen(false);
      setSelectedCommentId(null);
    }
  };

  const toggleExpand = (id) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleReplySend = async (commentId) => {
    if (!replyText.trim() || replyOpenId !== commentId) {
      alert("Reply text is required");
      return;
    }

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/${role}/comments/reply/${commentId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ reply: replyText }),
          credentials: "include",
        }
      );

      if (res.ok) {
        // const newReply = await res.json();

        fetchReplies(commentId);
        fetchComments();

        setReplyText("");
        setReplyOpenId(null);
      } else {
        const err = await res.json();
        alert(err.error || "Failed to send reply");
      }
    } catch (err) {
      console.error("Failed to send reply", err);
      alert("An error occurred.");
    }
  };

  return (
    <>
      <Box className="comments-page">
        <Box
          className="header"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h5" fontWeight={500} mb={3}>
            Comments
          </Typography>

          <div
            className="add-button"
            onClick={() => {
              setShowSidebar(true);
            }}
          >
            <CommentOutlinedIcon /> Add comment
          </div>
        </Box>

        <Box className="cards-comment">
          {Array.isArray(comments) && comments.length === 0 ? (
            <Typography
              variant="body1"
              color="text.secondary"
              textAlign="left"
              mt={4}
            >
              No comments available.
            </Typography>
          ) : (
            Array.isArray(comments) &&
            comments.map((comment) => {
              const isExpanded = expandedIds.includes(comment.id);
              const isReplying = replyOpenId === comment.id;

              const commentText = comment.comment || "";

              // const previewText =
              //   commentText.length > 200 && !isExpanded
              //     ? commentText.slice(0, 200) + "..."
              //     : commentText;

              return (
                <Card
                  className="card"
                  key={comment.id}
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: 3,
                    boxShadow: "0px 2px 10px rgba(0,0,0,0.05)",
                  }}
                >
                  <CardContent>
                    <Box mb={1}>
                      <Typography variant="subtitle2" fontWeight={500}>
                        {comment.user_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {comment.comment_email}
                      </Typography>
                    </Box>

                    <Typography
                      variant="body2"
                      fontWeight={400}
                      color="primary"
                    >
                      Project: {comment.project_name}
                    </Typography>

                    <Box
                      sx={{
                        backgroundColor: "#ffffff",
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                        p: 1.5,
                        mt: 1,
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ whiteSpace: "pre-wrap" }}
                      >
                        {role === "admin" ? comment.comment : comment.text}
                      </Typography>
                    </Box>

                    {commentText.length > 200 && (
                      <Button
                        size="small"
                        onClick={() => toggleExpand(comment.id)}
                        startIcon={
                          isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />
                        }
                      >
                        {isExpanded ? "Prikaži manje" : "Prikaži više"}
                      </Button>
                    )}

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: "block" }}
                    >
                      Posted: {formatDate(comment.created_at)}
                    </Typography>

                    {Array.isArray(comment.replies) &&
                      comment.replies.length > 0 && (
                        <Box mt={2} ml={2}>
                          {comment.replies.map((reply, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                backgroundColor: "#e3f2fd",
                                borderLeft: "4px solid #1976d2",
                                borderRadius: 2,
                                p: 1.5,
                                mt: 1,
                              }}
                            >
                              <Typography variant="subtitle2" fontWeight={600}>
                                {reply.replier_name}
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {reply.text}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {formatDate(reply.created_at)}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}

                    <Box display="flex" gap={1} mt={2}>
                      <IconButton
                        onClick={() => {
                          setReplyOpenId(comment.id);
                          setReplyText("");
                          fetchReplies(comment.id);
                        }}
                      >
                        <ReplyIcon style={{ color: "#0b41cd" }} />
                      </IconButton>

                      {role === "admin" && (
                        <IconButton
                          onClick={() => {
                            setSelectedCommentId(comment.id);
                            setModalOpen(true);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>

                    {isReplying && (
                      <Box mt={2}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          maxRows={6}
                          label="Your reply"
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                        />
                        <Box mt={1} display="flex" justifyContent="flex-end">
                          <Button
                            style={{ background: "#0b41cd", borderRadius: 10 }}
                            variant="contained"
                            endIcon={<SendIcon />}
                            onClick={() => handleReplySend(comment.id)}
                          >
                            Send
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </Box>
        <AddModal
          isComment
          open={showSidebar}
          onClose={() => setShowSidebar(false)}
          onUserAdded={() => {
            setShowSidebar(false);
            fetchComments();
            refreshNotifications?.();
          }}
        />
      </Box>

      <ConfirmDeleteModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedCommentId(null);
        }}
        onConfirm={handleDelete}
        itemName="comment"
      />
    </>
  );
}

export default Comments;
